
 // components/chatHistory.js
//
// Fetches encrypted chat history in pages.
// The caller can save these raw, worker-ready records to IndexedDB.
// Messages are returned oldest → newest.
//
// Important: GroupChat should call this only when its existing
// IndexedDB bootstrap logic determines that history needs seeding.

import axios from "axios";

const PAGE_SIZE = 100;
const MAX_PAGES_PER_REQUEST = 10000;

/* =========================================================
   fetchChatHistory
   Loads all available DM history in batches for an empty-IDB
   bootstrap. Each server response is limited to PAGE_SIZE.
   Returns raw server messages (oldest → newest).
========================================================= */
export const fetchChatHistory = async (peerChatId, myChatId) => {
  if (!peerChatId) return [];

  const allMessages = [];
  const seenIds = new Set();
  let before = null;
  let pageCount = 0;

  try {
    while (pageCount < MAX_PAGES_PER_REQUEST) {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
      });

      if (before) params.set("before", before);

      const res = await axios.get(
        `/api/chats/${encodeURIComponent(peerChatId)}/messages?${params.toString()}`,
        { withCredentials: true }
      );

      const page = res.data?.messages;

      if (!Array.isArray(page) || page.length === 0) {
        break;
      }

      for (const message of page) {
        const id = message?.id || message?.msgId || message?.messageId;

        // Keep messages without an ID rather than silently dropping them.
        if (!id) {
          allMessages.push(message);
        } else if (!seenIds.has(String(id))) {
          seenIds.add(String(id));
          allMessages.push(message);
        }
      }

      pageCount += 1;

      // Stop when the server says no older messages remain.
      if (res.data?.hasMore !== true || !res.data?.nextCursor) {
        break;
      }

      // The route's cursor points to the oldest message in this page.
      const nextBefore = String(res.data.nextCursor);

      // Guard against a bad or repeating cursor.
      if (nextBefore === before) {
        break;
      }

      before = nextBefore;
    }

    // Pages arrive newest → older. Return oldest → newest.
    allMessages.sort((a, b) => {
      const timeA = Number(a?.timestamp) || 0;
      const timeB = Number(b?.timestamp) || 0;

      if (timeA !== timeB) {
        return timeA - timeB;
      }

      return String(a?.id || a?.msgId || "").localeCompare(
        String(b?.id || b?.msgId || "")
      );
    });

    return allMessages;
  } catch {
    // Do not return partial history as if it were a complete bootstrap.
    // Returning [] preserves the existing caller's failure handling.
    return [];
  }
};

/* =========================================================
   fetchMoreChatHistory
   Cursor-based pagination for older messages (scroll-back).
   Pass beforeMsgId = oldest msgId currently loaded.
   Returns one raw page (oldest → newest within that chunk).
========================================================= */
export const fetchMoreChatHistory = async (
  peerChatId,
  myChatId,
  beforeMsgId,
  limit = 50
) => {
  if (!peerChatId || !beforeMsgId) return [];

  try {
    const safeLimit = Math.max(
      1,
      Math.min(Number(limit) || 50, PAGE_SIZE)
    );

    const params = new URLSearchParams({
      before: String(beforeMsgId),
      limit: String(safeLimit),
    });

    const res = await axios.get(
      `/api/chats/${encodeURIComponent(peerChatId)}/messages?${params.toString()}`,
      { withCredentials: true }
    );

    const messages = res.data?.messages;

    return Array.isArray(messages) ? messages : [];
  } catch {
    return [];
  }
};
